import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  AlertCallout,
  BarChartEvolution,
  BankAvatar,
  Box,
  HeaderHome,
  HeroCard,
  Icon,
  LockedFeatureOverlay,
  MetricCard,
  NoActivePlanBanner,
  PieChart,
  QuotesTicker,
  RiskChip,
  Screen,
  Tabs,
  Text,
  type BarChartEvolutionPoint,
  type PieChartSlice,
  type TabItem,
} from '~/atomic';
import { useAppDispatch, useAppSelector } from '~/store';
import {
  selectContractsList,
  selectDebtByBucket,
  selectDebtChangeVsPreviousMonth,
  selectNextInstallment,
  selectOverdueInstallments,
  selectTotalDebt,
  selectTotalPaidThisYear,
} from '~/store/contracts/selectors';
import { selectQuotesUf, selectVisibleQuoteItems } from '~/store/quotes/selectors';
import { selectUserData } from '~/store/user/selectors';
import { fetchContracts } from '~/store/contracts/thunks';
import { fetchQuotes } from '~/store/quotes/thunks';
import { latestMarketIndexesRequest } from '~/services';
import type { propsApiMarketIndexRate } from '~/services';
import { modalityLabel } from '~/utils/contractLabels';
import {
  formatCentsToBRL,
  formatCentsToBRLCompact,
} from '~/utils/formatFinance';
import { timeGreeting } from '~/utils/greeting';

type HomeChartTab = 'institution' | 'modality' | 'evolution';

const TAB_ITEMS: TabItem<HomeChartTab>[] = [
  { key: 'institution', label: 'Instituição' },
  { key: 'modality', label: 'Modalidade' },
  { key: 'evolution', label: 'Evolução' },
];

const MODALITY_COLORS: Record<string, string> = {
  custeio: '#1D4733',
  investimento: '#6DC48E',
  capital_giro: '#D98E2B',
  cprf: '#5F7CBE',
  comercializacao: '#7E5C9D',
  outros: '#718096',
};

const institutionColor = (name: string, shortCode?: string) => {
  const value = `${name} ${shortCode ?? ''}`.toLocaleLowerCase('pt-BR');
  if (value.includes('banco do brasil') || value.includes(' bb ')) return '#F7C600';
  if (value.includes('caixa')) return '#0066B3';
  if (value.includes('bradesco')) return '#B91C3D';
  if (value.includes('itaú') || value.includes('itau')) return '#EC7000';
  if (value.includes('santander')) return '#EC0000';
  if (value.includes('sicredi')) return '#338D35';
  if (value.includes('sicoob')) return '#007D68';
  if (value.includes('safra')) return '#174A3A';
  if (value.includes('btg')) return '#072E5A';
  if (value.includes('inter')) return '#FF7A00';
  if (value.includes('nubank')) return '#820AD1';
  if (value.includes('c6')) return '#202020';
  if (value.includes('xp')) return '#161616';
  if (value.includes('picpay')) return '#21C25E';
  const fallbackColors = ['#3B82A0', '#8C6A3B', '#596A8A', '#A35C7A', '#4C8B78', '#7A6CA5', '#AD7D3B'];
  const hash = [...value].reduce((total, char) => ((total * 31) + char.charCodeAt(0)) >>> 0, 0);
  return fallbackColors[hash % fallbackColors.length];
};

const splitMonetary = (cents: number) => {
  const total = formatCentsToBRL(cents);
  const lastComma = total.lastIndexOf(',');
  if (lastComma < 0) return { integer: total, decimal: '' };
  return { integer: total.slice(0, lastComma), decimal: total.slice(lastComma) };
};

const MONTH_ABBR = ['jan','fev','mar','abr','mai','jun','jul','ago','set','out','nov','dez'];
const formatDayMonth = (iso?: string) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return `${String(d.getUTCDate()).padStart(2, '0')} ${MONTH_ABBR[d.getUTCMonth()]}`;
};

const formatSignedCompact = (cents: number) => {
  if (cents === 0) return formatCentsToBRLCompact(0);
  const sign = cents > 0 ? '+' : '−';
  return `${sign}${formatCentsToBRLCompact(Math.abs(cents))}`;
};

export default function HomeView() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const user = useAppSelector(selectUserData);
  const totalDebt = useAppSelector(selectTotalDebt);
  const debtChange = useAppSelector(selectDebtChangeVsPreviousMonth);
  const paidThisYear = useAppSelector(selectTotalPaidThisYear);
  const nextInstallment = useAppSelector(selectNextInstallment);
  const buckets = useAppSelector(selectDebtByBucket);
  const allContracts = useAppSelector(selectContractsList);
  const overdues = useAppSelector(selectOverdueInstallments);
  const quoteItems = useAppSelector(selectVisibleQuoteItems);
  const quotesUf = useAppSelector(selectQuotesUf);

  const [chartTab, setChartTab] = useState<HomeChartTab>('institution');
  const [refreshing, setRefreshing] = useState(false);
  const [marketIndexes, setMarketIndexes] = useState<propsApiMarketIndexRate[]>([]);

  useEffect(() => {
    dispatch(fetchContracts());
    dispatch(fetchQuotes(null));
    latestMarketIndexesRequest().then((response) => setMarketIndexes(response.data)).catch(() => setMarketIndexes([]));
  }, [dispatch]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([
      dispatch(fetchContracts()),
      dispatch(fetchQuotes(quotesUf)),
      latestMarketIndexesRequest().then((response) => setMarketIndexes(response.data)).catch(() => setMarketIndexes([])),
    ]);
    setRefreshing(false);
  };

  const hasContracts = allContracts.length > 0;
  const greeting = useMemo(() => timeGreeting(), []);
  const totalSplit = useMemo(() => splitMonetary(totalDebt), [totalDebt]);
  const debtDirection: 'up' | 'down' | 'flat' =
    debtChange > 0 ? 'up' : debtChange < 0 ? 'down' : 'flat';

  const evolutionPoints = useMemo<BarChartEvolutionPoint[]>(() => {
    if (chartTab !== 'evolution' || !allContracts.length || totalDebt <= 0) return [];
    const currentYear = new Date().getFullYear();
    const earliestYear = allContracts.reduce((min, c) => {
      const y = new Date(c.startDate).getFullYear();
      return y < min ? y : min;
    }, currentYear);
    const startYear = Math.max(earliestYear, currentYear - 7);
    const effectiveStart = startYear >= currentYear ? currentYear - 1 : startYear;
    const years = Array.from(
      { length: currentYear - effectiveStart + 1 },
      (_, i) => effectiveStart + i,
    );
    const sumAmountTotal = allContracts.reduce((s, c) => s + c.amountTotal, 0);
    const sumAmountFinanced = allContracts.reduce((s, c) => s + c.amountFinanced, 0);
    const principalRatio = sumAmountTotal > 0 ? sumAmountFinanced / sumAmountTotal : 0.72;
    return years.map((year) => {
      const yearsAgo = currentYear - year;
      const ratio = 1 + yearsAgo * 0.12 + Math.sin(yearsAgo / 2.1) * 0.04;
      const projected = Math.max(0, Math.round((totalDebt || 0) * ratio));
      const principal = Math.round(projected * principalRatio);
      return { label: String(year), principal, interest: projected - principal };
    });
  }, [chartTab, totalDebt, allContracts]);

  const breakdown = useMemo<PieChartSlice[]>(() => {
    if (chartTab === 'evolution') return [];
    const map = new Map<string, PieChartSlice>();
    allContracts.forEach((c) => {
      const key = chartTab === 'modality' ? c.modality : c.institution.id;
      const label =
        chartTab === 'modality' ? modalityLabel(c.modality) : c.institution.name;
      const color = chartTab === 'modality'
        ? MODALITY_COLORS[c.modality] ?? MODALITY_COLORS.outros
        : institutionColor(c.institution.name, c.institution.shortCode);
      const prev = map.get(key) ?? { label, value: 0, color };
      map.set(key, { ...prev, value: prev.value + c.amountRemaining });
    });
    return [...map.values()].sort((a, b) => b.value - a.value);
  }, [chartTab, allContracts]);

  const contractsByBank = useMemo(() => {
    const map = new Map<string, { institution: (typeof allContracts)[0]['institution']; count: number }>();
    allContracts.forEach((c) => {
      const prev = map.get(c.institution.id);
      if (prev) prev.count += 1;
      else map.set(c.institution.id, { institution: c.institution, count: 1 });
    });
    return [...map.values()].sort((a, b) => b.count - a.count);
  }, [allContracts]);

  const overdueCount = overdues.length;
  const overdueAmount = overdues.reduce((s, i) => s + i.amount, 0);

  const goOpenContract = (id: string) => navigate(`/app/contracts/${id}`);
  const goOpenList = () => navigate('/app/contracts');
  const goOpenAlerts = () => navigate('/app/alerts');
  const goOpenAdd = () => navigate('/app/add');
  const goOpenMarket = () => navigate('/app/market');
  const goOpenCalculator = () => navigate('/financeiro/calculadora');
  const webCalculatorUrl =
    typeof window === 'undefined'
      ? '/financeiro/calculadora'
      : `${window.location.origin}/financeiro/calculadora`;

  return (
    <Screen
      showHeader={false}
      hasPadding={false}
      hasTabNavigation
      refreshLoading={refreshing}
      handleRefresh={handleRefresh}
      bgClassName="bg-intense-02"
    >
      <HeaderHome
        userName={user.fullName || 'Produtor'}
        farmName={user.farm?.farmName}
        greeting={greeting}
        unreadCount={overdueCount}
      />
      <Box className="-mt-xl rounded-t-large bg-intense-02 px-small pt-medium flex flex-col gap-medium pb-large min-w-0 overflow-x-hidden">
        <QuotesTicker
          items={quoteItems}
          marketIndexes={marketIndexes}
          uf={quotesUf}
          onClickItem={goOpenMarket}
          onClickHeader={goOpenMarket}
        />

        <NoActivePlanBanner onClickCta={() => navigate('/app/subscribe')} />

        {overdueCount > 0 ? (
          <AlertCallout
            variant="critical"
            title={`${overdueCount} ${overdueCount === 1 ? 'parcela vencida' : 'parcelas vencidas'} · ${formatCentsToBRLCompact(overdueAmount)}`}
            description="Pague em até 7 dias para evitar negativação."
            ctaText="Ver pendências"
            onClickCta={goOpenAlerts}
          />
        ) : null}

        {!hasContracts ? (
          <HeroCard className="rounded-large">
            <Box className="px-small pt-large pb-medium flex flex-col items-center text-center">
              <Box className="px-smaller py-quark rounded-full bg-intense-01/15 border border-intense-01/25">
                <Text className="text-captionLG color-intense-01">COMECE EM 1 MINUTO</Text>
              </Box>
              <Box className="mt-medium h-[128px] w-[140px] flex items-center justify-center">
                <Box className="w-[96px] h-[120px] rounded-medium bg-intense-01 flex items-center justify-center" shadow="level2">
                  <Icon name="icon-document" size={40} color="#1D4733" rounded={false} />
                </Box>
              </Box>
              <Text className="mt-medium text-headingH1 color-intense-01" as="p">
                Organize seus contratos em um só lugar
              </Text>
              <Text className="mt-quark text-bodySM color-intense-01" as="p">
                Envie o PDF do contrato ou cadastre manualmente para acompanhar
                parcelas, riscos e indicadores em tempo real.
              </Text>
              <button
                type="button"
                onClick={goOpenAdd}
                className="mt-medium w-full h-[56px] rounded-small bg-intense-01 flex items-center justify-center gap-nano shadow-level1"
              >
                <Icon name="icon-ai" size={20} color="#1D4733" rounded={false} />
                <Text className="text-paragraphLG color-primary-01">Adicionar contrato</Text>
              </button>
              <button
                type="button"
                onClick={goOpenCalculator}
                className="w-full h-[56px] rounded-small bg-primary-01 text-intense-01 flex items-center justify-center gap-nano"
              >
                <Icon name="icon-ai" size={20} color="#ffffff" rounded={false} />
                <Text className="text-paragraphLG color-intense-01">Abrir calculadora de financiamento</Text>
              </button>
              <a
                href={webCalculatorUrl}
                target="_blank"
                rel="noreferrer"
                className="w-full h-[56px] rounded-small border border-intense-03 flex items-center justify-center text-paragraphLG color-primary-01 bg-intense-02"
              >
                Acessar no navegador
              </a>
            </Box>
          </HeroCard>
        ) : null}

        {!hasContracts ? null : (
          <Box className="flex flex-col gap-small">
            <Text className="text-paragraphLG color-complement-01">
              Central de simulações
            </Text>
            <button
              type="button"
              onClick={goOpenCalculator}
              className="w-full h-[56px] rounded-medium bg-intense-01 border border-intense-03 flex items-center justify-center gap-nano"
            >
              <Icon name="icon-ai" size={20} color="#1D4733" rounded={false} />
              <Text className="text-paragraphLG color-primary-01">
                Abrir calculadora de financiamento
              </Text>
            </button>
            <a
              href={webCalculatorUrl}
              target="_blank"
              rel="noreferrer"
              className="w-full text-center h-[48px] rounded-medium bg-intense-01 border border-intense-03 flex items-center justify-center text-paragraphLG color-primary-01 hover:border-primary-01 transition-colors"
            >
              Acessar no navegador
            </a>
          </Box>
        )}

        {hasContracts ? (
          <MetricCard
            label="Endividamento total"
            value={totalSplit.integer}
            decimals={totalSplit.decimal}
            delta={{
              value: formatSignedCompact(debtChange),
              direction: debtDirection,
              style: 'pill',
              note: 'vs. mês anterior',
            }}
            footer={[
              { label: `Pago em ${new Date().getFullYear()}`, value: formatCentsToBRLCompact(paidThisYear) },
              {
                label: 'Próximo vencimento',
                value: nextInstallment
                  ? `${formatCentsToBRLCompact(nextInstallment.amount)} · ${formatDayMonth(nextInstallment.dueDate)}`
                  : '—',
                valueClassName: 'color-warning-04',
              },
            ]}
            onClick={goOpenAlerts}
          />
        ) : null}

        {hasContracts ? (
          <Box className="flex flex-col gap-smaller">
            <div className="flex items-center justify-between gap-smaller min-w-0">
              <Text className="text-paragraphLG color-complement-01 min-w-0" numberOfLines={1}>Endividamento por prazo</Text>
              <Text className="text-captionLG color-primary-01 shrink-0" onClick={goOpenList}>Ver tudo</Text>
            </div>
            <button
              type="button"
              onClick={goOpenList}
              className="w-full text-left rounded-medium bg-primary-01 p-small shadow-level1 min-w-0"
            >
              <Text className="text-bodyMD color-intense-04 uppercase" numberOfLines={1}>{buckets[0]?.label}</Text>
              <Text className="mt-nano text-headingH1 color-intense-01 break-words">
                {formatCentsToBRLCompact(buckets[0]?.amount ?? 0)}
              </Text>
              <div className="mt-nano flex items-center justify-between gap-quark min-w-0">
                <Text className="text-captionMD color-intense-03 min-w-0" numberOfLines={1}>
                  {buckets[0]?.contractsCount === 0
                    ? 'Nenhum contrato'
                    : `${buckets[0]?.contractsCount} ${buckets[0]?.contractsCount === 1 ? 'contrato' : 'contratos'}`}
                </Text>
                {buckets[0]?.contractsCount > 0 ? <RiskChip level={buckets[0].riskLevel} compact /> : null}
              </div>
            </button>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-smaller">
              {buckets.slice(1).map((b) => (
                <button
                  key={b.key}
                  type="button"
                  onClick={goOpenList}
                  className="rounded-medium bg-intense-01 border border-intense-03 p-small text-left shadow-level1 min-w-0"
                >
                  <Text className="text-captionLG color-intense-06 uppercase" numberOfLines={1}>{b.label}</Text>
                  <Text className="mt-quark text-paragraphLG color-complement-01 break-words">
                    {formatCentsToBRLCompact(b.amount)}
                  </Text>
                  <div className="mt-nano flex items-center justify-between gap-quark min-w-0">
                    <Text className="text-captionMD color-intense-06 min-w-0" numberOfLines={1}>
                      {b.contractsCount === 0
                        ? 'Nenhum'
                        : `${b.contractsCount} ${b.contractsCount === 1 ? 'contrato' : 'contratos'}`}
                    </Text>
                    {b.contractsCount > 0 ? <RiskChip level={b.riskLevel} compact /> : null}
                  </div>
                </button>
              ))}
            </div>
          </Box>
        ) : null}

        {hasContracts ? (
          <LockedFeatureOverlay
            feature="dashboard_analytics"
            title="Analytics avançado"
            description="Acompanhe a evolução do saldo devedor e a quebra por modalidade e instituição. Disponível nos planos pagos."
          >
            <Box className="rounded-medium bg-intense-01 px-smaller py-middle">
              <Tabs items={TAB_ITEMS} active={chartTab} onChange={setChartTab} />
              <div className="mt-smaller">
                {chartTab === 'evolution' ? (
                  <>
                    <Text className="text-captionLG color-intense-06 uppercase">
                      Saldo devedor — por ano
                    </Text>
                    <Text className="mt-quark text-headingH1 color-complement-01">
                      {formatCentsToBRLCompact(totalDebt)}
                    </Text>
                    <BarChartEvolution points={evolutionPoints} height={190} />
                  </>
                ) : (
                  <>
                    <Text className="text-captionLG color-intense-06 uppercase">
                      {chartTab === 'institution' ? 'Saldo devedor — por instituição' : 'Saldo devedor — por modalidade'}
                    </Text>
                    <PieChart slices={breakdown} formatValue={formatCentsToBRLCompact} />
                  </>
              )}
              </div>
            </Box>
          </LockedFeatureOverlay>
        ) : null}

        {hasContracts ? (
          <Box className="flex flex-col gap-smaller">
            <div className="flex items-center justify-between gap-smaller min-w-0">
              <Text className="text-paragraphLG color-complement-01 min-w-0" numberOfLines={1}>
                Por banco
              </Text>
              <Text className="text-captionLG color-primary-01 shrink-0" onClick={goOpenList}>
                Ver todos
              </Text>
            </div>
            <div className="flex flex-col gap-quark">
              {contractsByBank.map(({ institution, count }) => (
                <button
                  key={institution.id}
                  type="button"
                  onClick={goOpenList}
                  className="w-full text-left flex items-center gap-smaller rounded-medium bg-intense-01 border border-intense-03 p-small shadow-level1 hover:shadow-level2 transition-shadow"
                >
                  <BankAvatar institution={institution} size="md" />
                  <div className="flex-1 min-w-0">
                    <Text className="text-paragraphMD color-complement-01" numberOfLines={1}>
                      {institution.name}
                    </Text>
                    <Text className="text-captionMD color-intense-06">
                      {count} {count === 1 ? 'contrato' : 'contratos'}
                    </Text>
                  </div>
                  <Icon name="icon-chevron-right" size={16} color="#A0A0A0" rounded={false} className="shrink-0" />
                </button>
              ))}
            </div>
          </Box>
        ) : null}
      </Box>
    </Screen>
  );
}
